import LanguageSelector from "../common/LanguageSelector";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "../../assets/css/auth.css";
import { BlackLogo } from "../common/BlackLogo";
import axios from "axios";

export default function UpdatePassword() {
  useTranslation();
  const history = useNavigate();
  const [email, setEmail]               = useState("");
  const [code, setCode]                 = useState("");
  const [password, setPassword]         = useState("");
  const [confirmPassword, setConfirm]   = useState("");
  const [hidden, setHidden]             = useState(true);
  const [hiddenConfirm, setHiddenConfirm] = useState(true);
  const [errors, setErrors]             = useState({});
  const [submitted, setSubmitted]       = useState(false);
  const [passwordCheck, setPasswordCheck] = useState({ length: false, letter: false, number: false, special: false });

  useEffect(() => {
    const url = new URL(window.location.href);
    const c   = url.searchParams.get("user");
    if (c) {
      const text = atob(c);
      const [decodedEmail] = text.split(" ");
      setEmail(decodedEmail);
      setCode(c);
    }
  }, []);

  const checkPasswordStrength = (val) => {
    setPasswordCheck({
      length : val.length >= 8,
      letter : /[a-z]/.test(val) && /[A-Z]/.test(val),
      number : /[0-9]/.test(val),
      special: /[!@#$%&]/.test(val),
    });
  };

  const validate = () => {
    const errs = {};
    let valid = true;
    if (!password) { errs.password = "Password is required."; valid = false; }
    else if (password.length < 8) { errs.password = "Password must be at least 8 characters."; valid = false; }
    else if (!/[A-Z]/.test(password)) { errs.password = "Must include at least one uppercase letter."; valid = false; }
    else if (!/[0-9]/.test(password)) { errs.password = "Must include at least one number."; valid = false; }
    if (!confirmPassword) { errs.confirmPassword = "Please confirm your password."; valid = false; }
    else if (password !== confirmPassword) { errs.confirmPassword = "Passwords do not match."; valid = false; }
    setErrors(errs);
    return valid;
  };

  const updatePassword = async () => {
    if (!validate()) return;
    try {
      const config = { headers: { "Content-Type": "application/json" } };
      const res = await axios.post("/api/profile/update_password", JSON.stringify({ password, email, code }), config);
      if (res.status === 200 && res.data.user === true) {
        setSubmitted(true);
        setTimeout(() => history("/login"), 3000);
      }
    } catch (error) {
      if (error.response?.data?.errors?.[0]) {
        setErrors({ password: error.response.data.errors[0].msg });
      }
    }
  };

  return (
    <div className="auth-root">
      {/* ── Left Panel ── */}
      <div className="auth-left">
        <div className="auth-logo"><BlackLogo /></div>
        <div className="auth-hero">
          <div className="auth-hero-badge">
            <span className="auth-hero-badge-dot"></span>{tr("Password Reset")} </div>
          <h1>{tr("Choose a new")} <span>{tr("password.")}</span></h1>
          <p>{tr("Pick a strong password to keep your Minutezilla account secure. You'll be signed in automatically after resetting.")}</p>
          <div className="auth-features">
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <span className="auth-feature-text">{tr("Minimum 8 characters")}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span className="auth-feature-text">{tr("Mix of uppercase, numbers & symbols")}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <span className="auth-feature-text">{tr("Your account stays protected")}</span>
            </div>
          </div>
        </div>
        <div className="auth-stats">
          <div className="auth-stat-item">
            <div className="auth-stat-value">SSL</div>
            <div className="auth-stat-label">{tr("Encrypted")}</div>
          </div>
          <div className="auth-stat-item">
            <div className="auth-stat-value">{tr("GDPR")}</div>
            <div className="auth-stat-label">{tr("Compliant")}</div>
          </div>
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="auth-right">
        <div className="auth-card">
          <LanguageSelector />
          <NavLink to="/login" className="auth-back-link">
            <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>{tr("Back to sign in")} </NavLink>

          <div className="auth-card-header">
            <h2>{tr("Set a new password")}</h2>
            <p>{tr("Enter and confirm your new password below.")}</p>
          </div>

          {submitted ? (
            <div className="auth-success-box">
              <div className="auth-success-icon">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div className="auth-success-text">
                <h4>{tr("Password updated!")}</h4>
                <p>{tr("Your password has been changed. Redirecting you to sign in…")}</p>
              </div>
            </div>
          ) : (
            <form className="auth-form" onSubmit={(e) => { e.preventDefault(); updatePassword(); }} noValidate>
              <div className="auth-field">
                <label className="auth-label" htmlFor="new-password">{tr("New password")}</label>
                <div className="auth-input-wrap">
                  <input
                    className={`auth-input auth-input-with-toggle${errors.password ? " is-error" : ""}`}
                    type={hidden ? "password" : "text"}
                    id="new-password"
                    placeholder={tr("Min. 8 characters")}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); checkPasswordStrength(e.target.value); }}
                    autoComplete="new-password"
                  />
                  <button type="button" className="auth-input-toggle" onClick={() => setHidden(!hidden)} tabIndex="-1">
                    {hidden
                      ? <svg viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
                {errors.password && <span className="auth-error-msg">{tr(errors.password)}</span>}
                <div className="auth-pw-check">
                  <span className={`auth-pw-pill${passwordCheck.length ? " active" : ""}`}>{tr("8+ chars")}</span>
                  <span className={`auth-pw-pill${passwordCheck.letter ? " active" : ""}`}>{tr("Uppercase")}</span>
                  <span className={`auth-pw-pill${passwordCheck.number ? " active" : ""}`}>{tr("Number")}</span>
                  <span className={`auth-pw-pill${passwordCheck.special ? " active" : ""}`}>{tr("Special")}</span>
                </div>
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="confirm-password">{tr("Confirm password")}</label>
                <div className="auth-input-wrap">
                  <input
                    className={`auth-input auth-input-with-toggle${errors.confirmPassword ? " is-error" : ""}`}
                    type={hiddenConfirm ? "password" : "text"}
                    id="confirm-password"
                    placeholder={tr("Re-enter your password")}
                    value={confirmPassword}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button type="button" className="auth-input-toggle" onClick={() => setHiddenConfirm(!hiddenConfirm)} tabIndex="-1">
                    {hiddenConfirm
                      ? <svg viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
                {errors.confirmPassword && <span className="auth-error-msg">{tr(errors.confirmPassword)}</span>}
              </div>

              <button type="submit" className="auth-submit-btn">{tr("Reset password")}</button>
            </form>
          )}

          <p className="auth-footer-link">{tr("Remembered it?")} <NavLink to="/login">{tr("Sign in")}</NavLink>
          </p>
        </div>
      </div>
    </div>
  );
}
